import React, { useEffect, useState } from 'react';
import { useNavigate }                from 'react-router-dom';

import { authStore }                  from '../api/client';
import { createSession, getAnalytics, listSessions } from '../api/sessions';
import { listSchedules }              from '../api/schedules';
import { listAllOrganizations }       from '../api/orgs';
import Avatar                          from '../components/ui/Avatar';
import MetricCard                      from '../components/dashboard/MetricCard';
import RiskDonut                       from '../components/dashboard/RiskDonut';
import FindingsBarChart                from '../components/dashboard/FindingsBarChart';
import UpcomingAgenda                  from '../components/dashboard/UpcomingAgenda';
import IntegrityStatusBadge            from '../components/IntegrityStatusBadge';

/**
 * DashboardHome - host overview / analytics page mounted at /dashboard.
 *
 * Phase 9 rebuild: top-row 4 metric cards (incl. WoW delta), middle row of
 * recent sessions + risk donut + findings bar chart, bottom upcoming agenda.
 * All charts are inline SVG / tailwind so no chart library is needed.
 */

function normalizeScheduleList(resData) {
  if (Array.isArray(resData)) return resData;
  return resData?.content ?? resData?.schedules ?? [];
}

function normalizeSessionList(resData) {
  if (Array.isArray(resData)) return resData;
  return resData?.content ?? [];
}

export default function DashboardHome() {
  const navigate = useNavigate();
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const role  = authStore.role;
    const email = authStore.user?.email;

    async function load() {
      console.log('[DashboardHome] loading dashboard data; role:', role);
      setError(null);

      try {
        if (role === 'SUPER_ADMIN') {
          const [analyticsRes, orgsRes] = await Promise.all([
            getAnalytics(),
            listAllOrganizations().catch(() => ({ data: [] })),
          ]);
          const orgRaw = orgsRes.data;
          const orgs = Array.isArray(orgRaw) ? orgRaw : (orgRaw?.content ?? orgRaw?.organizations ?? []);
          const d = analyticsRes.data || {};
          setData({
            ...d,
            heading: 'Platform Overview',
            eyebrow: 'Platform',
            totalOrgs: orgs.length,
            totalSessions: d.totalSessions ?? 0,
            totalCandidates: d.totalCandidates ?? d.uniqueCandidates ?? d.candidateCount ?? 0,
            totalFindings: d.totalFindings ?? 0,
            highRiskSessions: d.highRiskSessions ?? 0,
            activeSessions: d.activeSessions ?? 0,
            weekOverWeekDelta: d.weekOverWeekDelta,
            recentSessions: d.recentSessions || [],
            riskDistribution: d.riskDistribution || { NONE: 0, MEDIUM: 0, HIGH: 0, NOT_EVALUATED: 0 },
            topFindingTypes: d.topFindingTypes,
            findingsByCategory: d.findingsByCategory,
          });
          return;
        }

        if (role === 'ORG_ADMIN') {
          const [analyticsRes, schedRes] = await Promise.all([
            getAnalytics(),
            email ? listSchedules(email) : Promise.resolve({ data: [] }),
          ]);
          const d = analyticsRes.data || {};
          const schedules = normalizeScheduleList(schedRes.data);
          const pendingSchedules = schedules.filter(
            (s) => (s.status || '').toUpperCase() === 'SCHEDULED',
          ).length;
          setData({
            ...d,
            heading: 'Your Organization',
            eyebrow: 'Organization',
            pendingSchedules,
            totalSessions: d.totalSessions ?? 0,
            activeCandidates: d.activeCandidates ?? d.uniqueCandidates ?? d.candidateCount ?? 0,
            totalFindings: d.totalFindings ?? 0,
            highRiskSessions: d.highRiskSessions ?? 0,
            activeSessions: d.activeSessions ?? 0,
            weekOverWeekDelta: d.weekOverWeekDelta,
            recentSessions: d.recentSessions || [],
            riskDistribution: d.riskDistribution || { NONE: 0, MEDIUM: 0, HIGH: 0, NOT_EVALUATED: 0 },
            topFindingTypes: d.topFindingTypes,
            findingsByCategory: d.findingsByCategory,
          });
          return;
        }

        // INTERVIEWER (default host dashboard)
        const [analyticsRes, sessionsRes, schedRes] = await Promise.all([
          getAnalytics(),
          listSessions({ size: 500 }),
          email ? listSchedules(email) : Promise.resolve({ data: [] }),
        ]);
        const d = analyticsRes.data || {};
        const allSessions = normalizeSessionList(sessionsRes.data);
        const mine = allSessions.filter(
          (s) =>
            (s.createdBy || s.hostEmail || '').toLowerCase() === (email || '').toLowerCase(),
        );
        const schedules = normalizeScheduleList(schedRes.data);
        const upcoming = schedules.filter(
          (s) => (s.status || '').toUpperCase() === 'SCHEDULED',
        );
        const recentFromAnalytics = (d.recentSessions || []).filter(
          (s) =>
            (s.createdBy || s.hostEmail || '').toLowerCase() === (email || '').toLowerCase(),
        );
        const recentFallback = mine
          .slice()
          .sort(
            (a, b) =>
              new Date(b.startedAt || b.createdAt || 0) -
              new Date(a.startedAt || a.createdAt || 0),
          )
          .slice(0, 8)
          .map((s) => ({
            sessionId: s.sessionId || s.id,
            code: s.code || s.sessionCode,
            candidateName: s.candidateName,
            candidateEmail: s.candidateEmail,
            startedAt: s.startedAt || s.createdAt,
            status: s.status,
            integrityStatus: s.integrityStatus ?? null,
          }));
        const recentSessions =
          recentFromAnalytics.length > 0 ? recentFromAnalytics : recentFallback;

        const endedMine = mine.filter((s) => (s.status || '').toUpperCase() === 'ENDED');
        setData({
          ...d,
          heading: 'Your Dashboard',
          eyebrow: 'Personal',
          totalSessions: mine.length,
          highRiskSessions: mine.filter((s) => (s.integrityStatus || '').toUpperCase() === 'HIGH').length,
          totalFindings: mine.reduce(
            (acc, s) => acc + (s.findingCount ?? s.findingsCount ?? s.totalFindings ?? 0),
            0,
          ),
          activeSessions: mine.filter((s) => (s.status || '').toUpperCase() === 'ACTIVE').length,
          weekOverWeekDelta: d.weekOverWeekDelta,
          recentSessions,
          upcomingScheduleCount: upcoming.length,
          recentReportsCount: endedMine.length,
          riskDistribution: d.riskDistribution || { NONE: 0, MEDIUM: 0, HIGH: 0, NOT_EVALUATED: 0 },
          topFindingTypes: d.topFindingTypes,
          findingsByCategory: d.findingsByCategory,
        });
      } catch (e) {
        console.error('[DashboardHome] load failed:', e.message);
        setError(e.response?.data?.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);

  async function handleNewSession() {
    setCreating(true);
    try {
      const res = await createSession();
      const sessionId = res.data?.sessionId || res.data?.id;
      if (sessionId) navigate(`/session/${sessionId}`);
    } catch (e) {
      console.error('[DashboardHome] createSession failed:', e.message);
      alert(e.response?.data?.message || 'Could not create session');
    } finally {
      setCreating(false);
    }
  }

  if (loading) {
    return (
      <div className="page-pad page-y">
        <div className="animate-pulse space-y-4">
          <div className="h-32 rounded-2xl bg-surface border border-line" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="h-28 rounded-xl bg-surface border border-line" />
            <div className="h-28 rounded-xl bg-surface border border-line" />
            <div className="h-28 rounded-xl bg-surface border border-line" />
            <div className="h-28 rounded-xl bg-surface border border-line" />
          </div>
        </div>
      </div>
    );
  }
  if (error) return <div className="p-6 text-rose-600 text-sm font-medium">{error}</div>;

  // Risk distribution: prefer backend-supplied values, otherwise infer 0s.
  const distribution = data.riskDistribution || { NONE: 0, MEDIUM: 0, HIGH: 0, NOT_EVALUATED: 0 };

  // Top finding types: prefer Phase 9 list, else fall back to legacy map.
  const topFindings = Array.isArray(data.topFindingTypes) && data.topFindingTypes.length
    ? data.topFindingTypes
    : Object.entries(data.findingsByCategory || {})
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([type, count]) => ({ type, count }));

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = authStore.user?.fullName?.split(' ')[0] || '';
  const role = authStore.role;
  const dashHeading = data.heading || 'Overview';
  const dashEyebrow = data.eyebrow || 'Overview';

  const heroSubtitle =
    role === 'SUPER_ADMIN'
      ? 'Platform-wide usage and risk across every organization.'
      : role === 'ORG_ADMIN'
        ? 'Activity and risk for your organization.'
        : 'Your interviews, schedules, and reporting at a glance.';

  return (
    <div className="page-pad page-y space-y-6 max-w-[1400px] mx-auto">
      {/* Hero - light card + teal accent */}
      <section className="relative overflow-hidden rounded-2xl border border-line bg-surface shadow-xs">
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(800px 200px at 100% 0%, #16b3a3, transparent 55%)',
          }}
          aria-hidden="true"
        />
        <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-600">{dashEyebrow}</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-1 text-ink">
              {dashHeading}
            </h1>
            <p className="text-sm font-medium text-ink-soft mt-0.5">
              {greeting}{firstName ? `, ${firstName}!` : '!'}
            </p>
            <p className="text-sm text-ink-faint mt-1.5 max-w-lg">
              {heroSubtitle}
            </p>
          </div>
          <button
            onClick={handleNewSession}
            disabled={creating}
            type="button"
            className="inline-flex items-center gap-2 self-start sm:self-auto btn btn-md btn-primary
                       disabled:opacity-60"
          >
            {creating ? 'Starting…' : '+ New interview'}
          </button>
        </div>
      </section>

      {/* ── Top metric row (role-aware) ─────────────────────────────── */}
      <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-4">
        {role === 'SUPER_ADMIN' && (
          <>
            <MetricCard label="Total Organizations" value={data.totalOrgs ?? 0} icon="🏢" />
            <MetricCard label="Total Sessions" value={data.totalSessions ?? 0} icon="📋" />
            <MetricCard label="Total Candidates" value={data.totalCandidates ?? 0} icon="👥" />
            <MetricCard label="Total Findings" value={data.totalFindings ?? 0} icon="🔍" />
          </>
        )}
        {role === 'ORG_ADMIN' && (
          <>
            <MetricCard label="Sessions (Org)" value={data.totalSessions ?? 0} icon="📋" />
            <MetricCard label="Active Candidates" value={data.activeCandidates ?? 0} icon="👥" />
            <MetricCard
              label="Sessions with high review signals"
              value={data.highRiskSessions ?? 0}
              icon="📊"
            />
            <MetricCard label="Pending Schedules" value={data.pendingSchedules ?? 0} icon="📅" />
          </>
        )}
        {role !== 'SUPER_ADMIN' && role !== 'ORG_ADMIN' && (
          <>
            <MetricCard label="My Sessions" value={data.totalSessions ?? 0} icon="📋" />
            <MetricCard label="Upcoming Schedules" value={data.upcomingScheduleCount ?? 0} icon="📅" />
            <MetricCard label="Recent Reports" value={data.recentReportsCount ?? 0} icon="📄" />
            <MetricCard
              label="Active Now"
              value={data.activeSessions ?? 0}
              accent="text-teal-600"
              delta={data.weekOverWeekDelta}
              icon="🟢"
            />
          </>
        )}
      </div>

      {/* ── Middle row: recent sessions + risk donut ─ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6">

        {/* Recent sessions */}
        <div className="card p-5 lg:col-span-2">
          <h2 className="font-heading text-base font-semibold text-ink mb-4">Recent Sessions</h2>
          {(data.recentSessions || []).length === 0 ? (
            <p className="text-xs text-ink-faint text-center py-8">No sessions yet</p>
          ) : (
            <div className="space-y-1.5">
              {data.recentSessions.map(s => (
                <div
                  key={s.sessionId}
                  onClick={() => navigate(s.status === 'ENDED' ? `/report/${s.sessionId}` : `/session/${s.sessionId}`)}
                  className="list-row rounded-xl p-2.5 flex items-center gap-3 cursor-pointer"
                >
                  <Avatar
                    name={s.candidateName || s.candidateEmail || '?'}
                    email={s.candidateEmail || ''}
                    size="sm"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-ink truncate">
                      {s.candidateName || s.candidateEmail || 'Unknown candidate'}
                    </p>
                    <p className="text-xs font-mono text-ink-faint truncate">
                      {s.code} · {s.startedAt ? new Date(s.startedAt).toLocaleDateString() : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <IntegrityStatusBadge status={s.integrityStatus} compact light />
                    <span className={s.status === 'ACTIVE' ? 'chip-teal text-[10px]' : 'chip-navy text-[10px]'}>
                      {s.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Risk donut */}
        <div className="card p-5">
          <h2 className="font-heading text-base font-semibold text-ink mb-4">Integrity Status (unreviewed signals)</h2>
          <RiskDonut distribution={distribution} />
        </div>
      </div>

      {/* ── Findings + Agenda row ──────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-base font-semibold text-ink">Top Detection Categories</h2>
            <span className="text-xs font-mono text-ink-faint">{data.totalFindings ?? 0} total</span>
          </div>
          <FindingsBarChart items={topFindings} />
        </div>

        <div className="card p-5">
          <h2 className="font-heading text-base font-semibold text-ink mb-4">Upcoming Interviews</h2>
          <UpcomingAgenda />
        </div>
      </div>
    </div>
  );
}
